import { TriangleAlert } from 'lucide-react';
import type { ReactNode, Ref } from 'react';
import { ErrorCodes, errorCode, errorSentence } from '../../components/ErrorToast';
import { Button, Card, EmptyState } from '../../components/ui';
import { ApiError } from '../../lib/errors';
import { LABEL } from './texts';

/*
 * Общие части разделов страницы «Мои ИИ-клиенты» (005, T147): «Подключения», «Мои выборки» и раздел «Компьютеры»,
 * который добавит 006. У каждого раздела один вид: заголовок второго уровня с числом, сведения «подпись — значение»,
 * ошибка загрузки с кодом и «Повторить».
 */

/** Сбой связи, сервера или ответ не по схеме — в серверный журнал (принцип II); отказ по правам и `404` — нет. */
export const shouldReport = (e: unknown) =>
  !(e instanceof ApiError) || e.code === 'NETWORK' || e.code === 'INTERNAL' || e.status >= 500;

/** Число рядом с заголовком; ноль не показывается (дизайн-система, «Счётчик»). */
export function Count({ n }: { n: number }) {
  if (n <= 0) return null;
  return (
    <span className="rounded-full bg-subtle px-2 py-0.5 text-[12px] font-medium text-ink-2 tabular-nums ring-1 ring-line ring-inset">
      {n}
    </span>
  );
}

/** Заголовок раздела: название и число. `ref` — фокус после действия (заголовок называет новое число). */
export function SectionHeading({
  id,
  title,
  count,
  ref,
}: {
  id: string;
  title: string;
  count: number;
  ref?: Ref<HTMLHeadingElement>;
}) {
  return (
    <h2
      id={id}
      ref={ref}
      tabIndex={ref ? -1 : undefined}
      className="flex items-center gap-2 text-[14px] font-semibold text-ink outline-none"
    >
      {title}
      <Count n={count} />
    </h2>
  );
}

/** Сведение: подпись сверху, значение под ней. */
export function Fact({
  label,
  className = '',
  children,
}: { label: string; className?: string; children: ReactNode }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="text-[12px] text-ink-3">{label}</dt>
      <dd className="mt-0.5 text-[13.5px] break-words text-ink">{children}</dd>
    </div>
  );
}

/**
 * Код ошибки (requestId) для команды разработки; его можно выделить и скопировать. Если текст ошибки — слова сервера,
 * рядом — код ответа (`errorCode`, итоговое ревью T160).
 */
export function ErrorCode({ error }: { error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  if (!requestId && !code) return null;
  return (
    <p className="text-[12px] text-ink-3">
      {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} className="text-ink-2" />
    </p>
  );
}

/** Раздел не загрузился: причина, код ошибки и «Повторить». */
export function LoadError({
  title,
  error,
  busy,
  onRetry,
}: { title: string; error: unknown; busy: boolean; onRetry: () => void }) {
  return (
    <Card>
      <EmptyState
        icon={TriangleAlert}
        title={title}
        description={errorSentence(error)}
        action={
          <div className="flex flex-col items-center gap-3">
            <ErrorCode error={error} />
            <Button variant="primary" disabled={busy} onClick={onRetry}>
              {LABEL.retry}
            </Button>
          </div>
        }
      />
    </Card>
  );
}
